import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, RefreshCw, Copy, Check, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { ApiKeyService, IntegrationActivityLogger } from '../services/partnerIntegrationService';
import { PartnerApiKey } from '../types';

interface ApiKeysConfigProps {
  partnerId: string;
  userId?: string;
  userName?: string;
}

export const ApiKeysConfig: React.FC<ApiKeysConfigProps> = ({
  partnerId,
  userId,
  userName
}) => {
  const [apiKeys, setApiKeys] = useState<PartnerApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // New key form
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState({
    read_campaigns: true,
    write_campaigns: false,
    read_advertisements: true,
    write_advertisements: false,
    read_promo_requests: true,
    write_promo_requests: false,
    read_analytics: true
  });

  useEffect(() => {
    loadApiKeys();
  }, [partnerId]);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const keys = await ApiKeyService.getKeys(partnerId);
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const newKey = await ApiKeyService.createKey(partnerId, {
        api_key_name: newKeyName || 'Default',
        ...newKeyPermissions
      });

      if (newKey) {
        setApiKeys([newKey, ...apiKeys]);
        setShowCreateModal(false);
        setNewKeyName('');
        setMessage({ type: 'success', text: 'API key created successfully! Copy it now - you won\'t see it again.' });
        
        // Show the new key
        setVisibleKeys(new Set([newKey.id]));

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'api_access',
          'created',
          userId,
          userName,
          { api_key_name: newKey.api_key_name }
        );

        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to create API key' });
    }
  };

  const handleRegenerateKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) {
      return;
    }

    try {
      const newKey = await ApiKeyService.regenerateKey(keyId);
      if (newKey) {
        setApiKeys(apiKeys.map(k => k.id === keyId ? newKey : k));
        setVisibleKeys(new Set([newKey.id]));
        setMessage({ type: 'success', text: 'API key regenerated! Copy the new key now.' });

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'api_access',
          'updated',
          userId,
          userName,
          { action: 'regenerated', api_key_id: keyId }
        );

        setTimeout(() => setMessage(null), 5000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to regenerate API key' });
    }
  };

  const handleDeleteKey = async (keyId: string, keyName?: string) => {
    if (!confirm(`Are you sure you want to delete the API key "${keyName || 'Unnamed'}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const success = await ApiKeyService.deleteKey(keyId);
      if (success) {
        setApiKeys(apiKeys.filter(k => k.id !== keyId));
        setMessage({ type: 'success', text: 'API key deleted successfully' });

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'api_access',
          'deleted',
          userId,
          userName,
          { api_key_name: keyName }
        );

        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to delete API key' });
    }
  };

  const handleToggleEnabled = async (key: PartnerApiKey) => {
    try {
      const updated = await ApiKeyService.updateKey(key.id, { enabled: !key.enabled });
      if (updated) {
        setApiKeys(apiKeys.map(k => k.id === key.id ? updated : k));
        setMessage({ type: 'success', text: `API key ${updated.enabled ? 'enabled' : 'disabled'}` });

        // Log activity
        await IntegrationActivityLogger.log(
          partnerId,
          'api_access',
          updated.enabled ? 'enabled' : 'disabled',
          userId,
          userName,
          { api_key_name: key.api_key_name }
        );

        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to update API key' });
    }
  };

  const copyToClipboard = (text: string, keyId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleKeyVisibility = (keyId: string) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId);
    } else {
      newVisible.add(keyId);
    }
    setVisibleKeys(newVisible);
  };

  const maskApiKey = (key: string | undefined) => {
    if (!key) return '••••••••••••••••••••••••••••••••';
    return key.substring(0, 12) + '••••••••••••••••••••';
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading API keys...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Key className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h4 className="text-lg font-medium text-gray-900">API Access</h4>
            <p className="text-sm text-gray-600">Manage API keys for programmatic access</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <AlertCircle className="h-5 w-5 mr-2" />
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Key className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No API Keys</h3>
          <p className="mt-1 text-sm text-gray-500">Create your first API key to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <div key={key.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h5 className="text-sm font-medium text-gray-900">{key.api_key_name || 'Unnamed Key'}</h5>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      key.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {key.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Created: {new Date(key.created_at).toLocaleDateString()}
                    {key.last_used_at && ` • Last used: ${new Date(key.last_used_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleEnabled(key)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title={key.enabled ? 'Disable' : 'Enable'}
                  >
                    <Key className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleRegenerateKey(key.id)}
                    className="p-1 text-gray-500 hover:text-blue-600"
                    title="Regenerate"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteKey(key.id, key.api_key_name)}
                    className="p-1 text-gray-500 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* API Key Display */}
              <div className="flex items-center space-x-2 mb-3">
                <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono">
                  {visibleKeys.has(key.id) ? key.api_key : maskApiKey(key.api_key)}
                </code>
                <button
                  onClick={() => toggleKeyVisibility(key.id)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                  title={visibleKeys.has(key.id) ? 'Hide' : 'Show'}
                >
                  {visibleKeys.has(key.id) ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => copyToClipboard(key.api_key, key.id)}
                  className="p-2 text-gray-500 hover:text-blue-600"
                  title="Copy"
                >
                  {copiedKey === key.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>

              {/* Permissions */}
              <div className="text-xs text-gray-600">
                <span className="font-medium">Permissions:</span>{' '}
                {[
                  key.read_campaigns && 'Read Campaigns',
                  key.write_campaigns && 'Write Campaigns',
                  key.read_advertisements && 'Read Ads',
                  key.write_advertisements && 'Write Ads',
                  key.read_promo_requests && 'Read Promos',
                  key.write_promo_requests && 'Write Promos',
                  key.read_analytics && 'Read Analytics'
                ].filter(Boolean).join(', ')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create New API Key</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Name
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production, Development"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions
                </label>
                <div className="space-y-2">
                  {[
                    { key: 'read_campaigns', label: 'Read Campaigns' },
                    { key: 'write_campaigns', label: 'Write Campaigns' },
                    { key: 'read_advertisements', label: 'Read Advertisements' },
                    { key: 'write_advertisements', label: 'Write Advertisements' },
                    { key: 'read_promo_requests', label: 'Read Promo Requests' },
                    { key: 'write_promo_requests', label: 'Write Promo Requests' },
                    { key: 'read_analytics', label: 'Read Analytics' }
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newKeyPermissions[key as keyof typeof newKeyPermissions]}
                        onChange={(e) => setNewKeyPermissions({
                          ...newKeyPermissions,
                          [key]: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
